import {
  BAR_CHART_OPTIONS,
  IS_RECENT,
  NONE,
  TOTAL,
} from '../../../common/utils/constants'
import {
  ChartElements,
  chartTypes,
  disaggregations,
  MissingDataBarTypes,
} from '../../../common/utils/enum'
import {
  clone,
  formatNumberOnUnitMeasure,
  formatValues,
  formatUnitMeasure,
  getNameFromDisaggregation,
  applyAxisRange,
  isValueOutOfRangeForRelAxis,
  getUnitMeasureItems,
} from '../../../common/utils/helper'
import { COLOR_MAP } from '../../../common/utils/theme'
import { getBenchmarkList } from '../common/helper'


const removeCountriesForRemoveGaps = (seriesData) => {
  const indicesToRemove = []
  for (let i = 0; i < seriesData[0].data.length; i++) {
    const allNull = seriesData.every((series) => series.data[i]?.y === null)
    if (allNull) {
      indicesToRemove.push(i)
    }
  }
  let updatedDataAfterNullsRemoved = null
  if (indicesToRemove.length > 0) {
    updatedDataAfterNullsRemoved = seriesData.map((series) => ({
      ...series,
      data: series.data.filter((_, index) => !indicesToRemove.includes(index)),
    }))
  }
  return updatedDataAfterNullsRemoved ?? seriesData
}

const getSortedIndices = ({
  filteredCountries,
  finalSelectedAverages,
  seriesData,
  selectedCountries,
}) => {
  const firstSeriesData = seriesData[0]?.data
  const indices = firstSeriesData.map((_, index) => index)

  indices.sort((a, b) => {
    const selectedCountriesWithoutAll = selectedCountries.filter(
      (c) => c !== 'all'
    )
    const countryA = filteredCountries[a].code
    const countryB = filteredCountries[b].code

    const aIsSelected = selectedCountriesWithoutAll.includes(countryA)
    const bIsSelected = selectedCountriesWithoutAll.includes(countryB)

    if (finalSelectedAverages && finalSelectedAverages.length > 0) {
      if (finalSelectedAverages.includes(countryA)) return -1
      if (finalSelectedAverages.includes(countryB)) return 1
    }

    if (aIsSelected && !bIsSelected) return -1
    if (!aIsSelected && bIsSelected) return 1

    const aData = firstSeriesData[a]
    const bData = firstSeriesData[b]

    const aIsValueNull = aData ? aData.isValueNull : true
    const bIsValueNull = bData ? bData.isValueNull : true

    if (aIsValueNull && !bIsValueNull) return 1
    if (!aIsValueNull && bIsValueNull) return -1

    const aY = aData ? aData.y : 0
    const bY = bData ? bData.y : 0

    return bY - aY
  })

  return indices
}
export const generateBarChartOptions = (
  indicatorData,
  indicatorMetadata,
  indicatorCode,
  disaggregationCountries,
  selectedCountries,
  selectedSingleYear,
  selectedComparison,
  disaggregationData,
  selectedFilters,
  recentYearSelection,
  filteredAggregateData,
  countryHierarchy,
  countryList,
  unitMeasureName,
  selectedUnitMeasure,
  unitMultName = '',
  selectedAverages,
  selectedMinYear,
  selectedMaxYear,
  isDownload = false,
  unitMeasureList,
  site,
  isFullScreenMode = false
) => {
  let seriesData = [],
    categories = [],
    filteredCountries = []
  const unitMeasureItems = getUnitMeasureItems(disaggregationData)
  const shortLabel = unitMeasureItems?.find(
    (item) => item.code === selectedUnitMeasure
  )?.short_label
  let allValuesNull = true
  let finalSelectedAverages = getBenchmarkList({
    selectedAverages,
    selectedCountries: selectedCountries?.filter((item) => item !== 'all'),
    countryHierarchy,
    chartType: chartTypes.Bar,
    disaggregationCountries,
  })

  let indicatorDataForChart = clone(indicatorData)
  if (filteredAggregateData && filteredAggregateData.length > 0) {
    indicatorDataForChart = [...indicatorDataForChart, ...filteredAggregateData]
  }
  // advanced visualization: missing data logic -- start
  let missingDataType = ''
  if (
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data
      ?.length &&
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data[0]
      ?.bar
  ) {
    missingDataType =
      indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data[0]
        ?.bar
  }
  // advanced visualization: missing data logic -- end
  if (selectedCountries.length > 0 && selectedCountries[0] === 'all') {
    const filteredDisaggregationCountries =  countryList?.map((country) => ({
          code: country.id,
          description: country.name,
        }))

    categories = filteredDisaggregationCountries.map(
      (country) => country.description
    )
    filteredCountries = clone(filteredDisaggregationCountries)

    if (finalSelectedAverages && finalSelectedAverages.length > 0) {
      let selectedAverageDetails = []
      finalSelectedAverages.forEach((item) => {
        const validDataForIndicator = disaggregationCountries.find(
          (country) => country.code === item
        )
        if (validDataForIndicator) {
          selectedAverageDetails.push(validDataForIndicator)
        }
      })

      filteredCountries.unshift(...selectedAverageDetails)
      categories.unshift(
        ...selectedAverageDetails?.map((item) => item?.description)
      )
    }
    if (selectedCountries.length > 1 && selectedComparison !== NONE) {
      const selectedCountryList = filteredCountries.filter((item) =>
        selectedCountries.includes(item.code)
      )
      const remainingList = filteredCountries.filter(
        (item) =>
          !selectedCountries.includes(item.code) &&
          !finalSelectedAverages.includes(item.code)
      )
      filteredCountries =
        finalSelectedAverages && finalSelectedAverages.length > 0
          ? [
              ...filteredCountries.filter((item) =>
                finalSelectedAverages.includes(item.code)
              ),
              ...selectedCountryList,
              ...remainingList,
            ]
          : [...selectedCountryList, ...remainingList]
      categories =
        finalSelectedAverages && finalSelectedAverages.length > 0
          ? [
              ...filteredCountries
                .filter((item) => finalSelectedAverages.includes(item.code))
                .map((country) => country.description),
              ...selectedCountryList.map((country) => country.description),
              ...remainingList.map((country) => country.description),
            ]
          : [
              ...selectedCountryList.map((country) => country.description),
              ...remainingList.map((country) => country.description),
            ]
    }
  } else {
    // color scheme logic -- start
    // mapping = fetchCountryMappingForColor(
    //   countryHierarchy?.REGION || [],
    //   selectedCountries
    // )
    // color scheme logic -- end
    selectedCountries?.forEach((selectedCountry) => {
      const country = disaggregationCountries.find(
        (country) => country.code === selectedCountry
      )
      if (country) {
        categories.push(country.description)
        filteredCountries.push(country)
      }
    })
    if (finalSelectedAverages && finalSelectedAverages.length > 0) {
      const selectedAverageDetails = finalSelectedAverages
        .map((item) =>
          disaggregationCountries.find((country) => country.code === item)
        )
        .filter(Boolean)
      if (selectedAverageDetails && selectedAverageDetails.length > 0) {
        filteredCountries.unshift(...selectedAverageDetails)
        categories.unshift(
          ...selectedAverageDetails?.map((item) => item?.description)
        )
      }
    }
  }

  let axisType = '',
    abs_min = '',
    abs_max = ''
  if (
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.axis_type !==
      'fix' &&
    (indicatorMetadata?.admin_metadata?.[site]?.visualization
      ?.axis_type_params?.[0]?.abs_min ||
      indicatorMetadata?.admin_metadata?.[site]?.visualization
        ?.axis_type_params?.[0]?.abs_max)
  ) {
    axisType = 'rel'
    abs_min =
      indicatorMetadata?.admin_metadata?.[site]?.visualization
        ?.axis_type_params?.[0]?.abs_min
    abs_max =
      indicatorMetadata?.admin_metadata?.[site]?.visualization
        ?.axis_type_params?.[0]?.abs_max
  }

  // advanced visualization: missing data logic -- start
  if (missingDataType === MissingDataBarTypes.RemoveGaps) {
    if (selectedComparison !== NONE) {
      const selectedValues =
        selectedFilters && selectedFilters[selectedComparison]
          ? selectedFilters[selectedComparison].split(',')
          : []
      if (indicatorDataForChart && indicatorDataForChart.length > 0) {
        let tempCategories = clone(filteredCountries)
        filteredCountries.forEach((category) => {
          const countryData = indicatorDataForChart.filter(
            (item) => item[disaggregations.RefArea] === category.code
          )
          if (!countryData || (countryData && countryData.length === 0)) {
            tempCategories = tempCategories.filter(
              (item) => item.code !== category.code
            )
            categories = categories.filter(
              (item) =>
                item.toLowerCase() !== category.description.toLowerCase()
            )
          } else {
          }
        })
        filteredCountries = tempCategories
        const years = recentYearSelection
          ? [IS_RECENT]
          : selectedMinYear === selectedMaxYear
            ? [selectedMinYear]
            : [selectedMinYear, selectedMaxYear]

        years.forEach((selectedYear) => {
          selectedValues.forEach((value, index) => {
            const name = getNameFromDisaggregation(
              disaggregationData?.dimensions,
              selectedComparison,
              value
            )
            let data = indicatorDataForChart.filter(
              (item) =>
                item[selectedComparison].toUpperCase() === value.toUpperCase()
            )
            let countriesData = []
            filteredCountries.forEach((category) => {
              const countryData = data.find(
                (item) =>
                  item[disaggregations.RefArea] === category.code &&
                  (selectedYear === IS_RECENT ||
                    item?.data?.xAxis?.some(
                      (year) => year.toString() === selectedYear.toString()
                    ))
              )

              let year =
                selectedYear === IS_RECENT
                  ? countryData?.data?.xAxis?.[0]
                  : selectedYear.toString()
              let countryValue = null
              if (selectedYear !== IS_RECENT) {
                const yearIndex = countryData?.data?.xAxis.findIndex(
                  (item) => item === selectedYear.toString()
                )
                countryValue = countryData?.data?.yAxis?.[yearIndex]
              } else {
                countryValue = countryData?.data?.yAxis?.[0]
              }

              countriesData.push({
                y:
                  countryValue &&
                  countryValue !== 'null' &&
                  countryValue !== 'NaN'
                    ? isValueOutOfRangeForRelAxis({
                        axisType,
                        abs_min,
                        abs_max,
                        value: countryValue,
                      })
                      ? null
                      : formatValues(countryValue)
                    : null,
                year,
                name: category?.description,
              })
            })
            // color scheme logic -- start
            let color = ''
            if (selectedComparison?.startsWith('COMP_BREAKDOWN')) {
              if (value === TOTAL) {
                color = COLOR_MAP[value]
              } else {
                let customDimensions = disaggregationData?.dimensions?.find(
                  (item) => item.field_name === selectedComparison
                )?.field_value
                customDimensions =
                  customDimensions.filter((item) => item.code !== TOTAL) || []
                const index = customDimensions.findIndex(
                  (item) => item.code === value
                )
                color = COLOR_MAP['category'][index]
              }
            } else if (selectedComparison.toLowerCase() === 'age') {
              if (value === TOTAL) {
                color = COLOR_MAP[value]
              } else {
                let ages = disaggregationData?.dimensions?.find(
                  (item) => item.field_name?.toLowerCase() === 'age'
                )?.field_value
                ages = ages?.filter((item) => item.code !== TOTAL)
                const index = ages.findIndex((item) => item.code === value)
                color = COLOR_MAP['age'][index % 5]
              }
            } else {
              color = COLOR_MAP[value]
            }
            // color scheme logic -- end
            seriesData.push({
              name:
                selectedYear === IS_RECENT ? name : `${name} (${selectedYear})`,
              data: countriesData,
              color,
              unitMeasureCode: data?.[disaggregations.UnitMeasure],
            })
          })
        })

        seriesData = removeCountriesForRemoveGaps(seriesData)

        if (seriesData.length > 0) {
          const indices = getSortedIndices({
            filteredCountries,
            finalSelectedAverages,
            seriesData,
            selectedCountries,
          })

          seriesData = seriesData.map((series) => {
            const data = indices.map((i) => series.data[i])
            return { ...series, data }
          })
          categories = seriesData[0]?.data?.map((item) => item.name)
        }
      }
    } else if (selectedComparison === NONE && recentYearSelection) {
      filteredCountries.forEach((country) => {
        const countryName = country.description
        const countryData = indicatorDataForChart.find(
          (data) => data[disaggregations.RefArea] === country.code
        )

        let countryValue = null
        let countryYear = ''
        if (countryData) {
          const year = countryData?.data?.xAxis?.[0]
          const value = countryData?.data?.yAxis?.[0]

          countryValue = value
          countryYear = year
        }

        if (countryValue && countryValue !== 'null' && countryValue !== 'NaN') {
          if (
            isValueOutOfRangeForRelAxis({
              axisType,
              abs_min,
              abs_max,
              value: countryValue,
            })
          ) {
            // do nothing
          } else {
            seriesData.push({
              name: countryName,
              y: formatValues(countryValue),
              color: COLOR_MAP.barChartNoneColor,
              countryCode: country.code,
              year: countryYear,
              unitMeasureCode: countryData?.[disaggregations.UnitMeasure],
            })
          }
        }
      })

      if (selectedCountries.length > 0 && selectedCountries.includes('all')) {
        if (selectedCountries.length === 1) {
          seriesData = [...seriesData].sort((a, b) => {
            if (finalSelectedAverages.includes(a.countryCode)) return -1
            if (finalSelectedAverages.includes(b.countryCode)) return 1
            return b.y - a.y
          })
          categories = seriesData.map((item) => item.name)
        } else if (selectedCountries.length > 1) {
          const data = [...seriesData]
          let countries = selectedCountries.filter((item) => item !== 'all')
          let selectedCountriesData = data.filter((item) =>
            countries.includes(item.countryCode)
          )
          let remainingCountriesData = data.filter(
            (item) =>
              !countries.includes(item.countryCode) &&
              !finalSelectedAverages.includes(item.countryCode)
          )
          selectedCountriesData.sort((a, b) => b.y - a.y)
          remainingCountriesData.sort((a, b) => {
            if (a.isValueNull && !b.isValueNull) return 1
            if (!a.isValueNull && b.isValueNull) return -1
            return b.y - a.y
          })
          seriesData =
            selectedAverages && selectedAverages.length > 0
              ? [
                  ...data.filter((item) =>
                    finalSelectedAverages.includes(item.countryCode)
                  ),
                  ...selectedCountriesData,
                  ...remainingCountriesData,
                ]
              : [...selectedCountriesData, ...remainingCountriesData]
          categories = seriesData.map((item) => item.name)
        }
        // color scheme logic -- start
        if (selectedCountries.length > 0 && selectedCountries[0] === 'all') {
          const x = [...seriesData]
          seriesData = x.map((data) => {
            return { ...data, color: COLOR_MAP.barChartNoneColor }
          })
        }
        // color scheme logic -- end
      } else {
        seriesData = [...seriesData].sort((a, b) => {
          if (finalSelectedAverages.includes(a.countryCode)) return -1
          if (finalSelectedAverages.includes(b.countryCode)) return 1
          return b.y - a.y
        })
        categories = seriesData.map((item) => item.name)
      }
    } else if (selectedComparison === NONE && !recentYearSelection) {
      const years =
        selectedMinYear === selectedMaxYear
          ? [selectedMinYear]
          : [selectedMinYear, selectedMaxYear]
      years.forEach((selectedYear) => {
        let tempData = []
        filteredCountries.forEach((country) => {
          const countryName = country.description
          const countryData = indicatorDataForChart.find(
            (data) =>
              data[disaggregations.RefArea] === country.code &&
              data?.data?.xAxis?.some(
                (year) => year.toString() === selectedYear.toString()
              )
          )

          let countryValue = null
          let countryYear = selectedYear.toString()
          if (countryData) {
            const yearIndex = countryData?.data?.xAxis?.findIndex(
              (item) => item === selectedYear.toString()
            )
            countryValue = countryData?.data?.yAxis?.[yearIndex]
          }
          let y = null
          if (
            countryValue &&
            countryValue !== 'null' &&
            countryValue !== 'NaN' &&
            !isValueOutOfRangeForRelAxis({
              axisType,
              abs_min,
              abs_max,
              value: countryValue,
            })
          ) {
            y = formatValues(countryValue)
          }
          tempData.push({
            name: countryName,
            y,
            color: COLOR_MAP.barChartNoneColor,
            countryCode: country.code,
            year: countryYear,
            unitMeasureCode: countryData?.[disaggregations.UnitMeasure],
          })
        })
        seriesData.push({
          name: `${selectedComparison} (${selectedYear})`,
          data: tempData,
        })
      })

      seriesData = removeCountriesForRemoveGaps(seriesData)
      const filteredCountriesAfterRemoveGaps = filteredCountries?.filter(
        (item) =>
          seriesData?.[0]?.data
            .map((dataItem) => dataItem?.countryCode)
            .includes(item?.code)
      )
      const indices = getSortedIndices({
        filteredCountries: filteredCountriesAfterRemoveGaps,
        finalSelectedAverages,
        seriesData,
        selectedCountries,
      })
      seriesData = seriesData.map((series) => {
        const data = indices.map((i) => series.data[i])
        return { ...series, data }
      })
      categories = seriesData[0]?.data?.map((item) => item.name)
    }
  }
  // advanced visualization: missing data logic -- end
  else {
    if (selectedComparison !== NONE) {
      const selectedValues =
        selectedFilters && selectedFilters[selectedComparison]
          ? selectedFilters[selectedComparison].split(',')
          : []
      const years = recentYearSelection
        ? [IS_RECENT]
        : selectedMinYear === selectedMaxYear
          ? [selectedMinYear]
          : [selectedMinYear, selectedMaxYear]
      years.forEach((selectedYear) => {
        selectedValues.forEach((value, index) => {
          const name = getNameFromDisaggregation(
            disaggregationData?.dimensions,
            selectedComparison,
            value
          )
          let data = indicatorDataForChart.filter(
            (item) =>
              item[selectedComparison].toUpperCase() === value.toUpperCase()
          )
          let countriesData = filteredCountries.map((category) => {
            const countryData = data.find(
              (item) =>
                item[disaggregations.RefArea] === category.code &&
                (selectedYear === IS_RECENT ||
                  item?.data?.xAxis?.some(
                    (year) => year.toString() === selectedYear.toString()
                  ))
            )

            let year =
              selectedYear === IS_RECENT
                ? countryData?.data?.xAxis?.[0]
                : selectedYear.toString()
            let countryValue = null
            if (selectedYear !== IS_RECENT) {
              const yearIndex = countryData?.data?.xAxis.findIndex(
                (item) => item === selectedYear.toString()
              )
              countryValue = countryData?.data?.yAxis?.[yearIndex]
            } else {
              countryValue = countryData?.data?.yAxis?.[0]
            }

            let isValueNull = false
            let isValueOutOfRange = false
            let outOfRangeValue = null

            if (
              isValueOutOfRangeForRelAxis({
                axisType,
                abs_min,
                abs_max,
                value: countryValue,
              })
            ) {
              outOfRangeValue = formatValues(countryValue)
              countryValue = 0
              isValueNull = true
              isValueOutOfRange = true
            }
            if (
              countryValue === '' ||
              countryValue === null ||
              countryValue === 'null' ||
              countryValue === 'NaN' ||
              countryValue === undefined
            ) {
              isValueNull = true
              countryValue = 0
            } else {
              allValuesNull = false
            }

            return {
              y:
                countryValue &&
                countryValue !== 'null' &&
                countryValue !== 'NaN'
                  ? formatValues(countryValue)
                  : 0,
              isValueNull,
              isValueOutOfRange,
              outOfRangeValue,
              year,
              unitMeasureCode: countryData?.[disaggregations.UnitMeasure],
              // dimension: name,
            }
          })
          // color scheme logic -- start
          let color = ''
          if (selectedComparison?.startsWith('COMP_BREAKDOWN')) {
            if (value === TOTAL) {
              color = COLOR_MAP[value]
            } else {
              let customDimensions = disaggregationData?.dimensions.find(
                (item) => item.field_name === selectedComparison
              )?.field_value
              customDimensions = customDimensions.filter(
                (item) => item.code !== TOTAL
              )
              const index = customDimensions.findIndex(
                (item) => item.code === value
              )
              color = COLOR_MAP['category'][index]
            }
          } else if (selectedComparison.toLowerCase() === 'age') {
            if (value === TOTAL) {
              color = COLOR_MAP[value]
            } else {
              let ages = disaggregationData?.dimensions?.find(
                (item) => item.field_name?.toLowerCase() === 'age'
              )?.field_value
              ages = ages.filter((item) => item.code !== TOTAL)
              const index = ages.findIndex((item) => item.code === value)
              color = COLOR_MAP['age'][index % 5]
            }
          } else {
            color = COLOR_MAP[value]
          }
          // color scheme logic -- end
          seriesData.push({
            name:
              selectedYear === IS_RECENT ? name : `${name} (${selectedYear})`,
            data: countriesData,
            color,
          })
        })
      })

      if (seriesData.length > 0) {
        const indices = getSortedIndices({
          filteredCountries,
          finalSelectedAverages,
          seriesData,
          selectedCountries,
        })

        categories = indices.map((i) => categories[i])

        seriesData = seriesData.map((series) => {
          const data = indices.map((i) => series.data[i])
          return { ...series, data }
        })
      }
    } else if (selectedComparison === NONE && recentYearSelection) {
      seriesData = filteredCountries.map((country) => {
        const countryName = country.description
        const countryData = indicatorDataForChart.find(
          (data) => data[disaggregations.RefArea] === country.code
        )

        let countryValue = null
        let isValueNull = false
        let isValueOutOfRange = false
        let outOfRangeValue = null
        let countryYear = ''
        if (countryData) {
          const year = countryData?.data?.xAxis?.[0]
          const value = countryData?.data?.yAxis?.[0]

          countryValue = value
          countryYear = year
          if (
            countryValue === '' ||
            countryValue === null ||
            countryValue === 'null' ||
            countryValue === 'NaN' ||
            countryValue === undefined
          ) {
            isValueNull = true
            countryValue = 0
          } else {
            if (
              isValueOutOfRangeForRelAxis({
                axisType,
                abs_min,
                abs_max,
                value: countryValue,
              })
            ) {
              outOfRangeValue = formatValues(countryValue)
              countryValue = 0
              isValueNull = true
              isValueOutOfRange = true
            } else {
              countryValue = formatValues(countryValue)
              allValuesNull = false
            }
          }
        } else {
          isValueNull = true
          countryValue = 0
        }

        return {
          name: countryName,
          y: countryValue,
          color: COLOR_MAP.barChartNoneColor,
          countryCode: country.code,
          isValueNull,
          isValueOutOfRange,
          outOfRangeValue,
          year: countryYear,
          unitMeasureCode: countryData?.[disaggregations.UnitMeasure],
        }
      })

      if (selectedCountries.length > 0 && selectedCountries.includes('all')) {
        if (selectedCountries.length === 1) {
          seriesData = [...seriesData].sort((a, b) => {
            if (finalSelectedAverages && finalSelectedAverages.length > 0) {
              if (finalSelectedAverages.includes(a.countryCode)) return -1
              if (finalSelectedAverages.includes(b.countryCode)) return 1
            }
            if (a.isValueNull && !b.isValueNull) return 1
            if (!a.isValueNull && b.isValueNull) return -1
            return b.y - a.y
          })
          categories = seriesData.map((item) => item.name)
        } else if (selectedCountries.length > 1) {
          const data = [...seriesData]
          let countries = selectedCountries.filter((item) => item !== 'all')
          let selectedCountriesData = data.filter((item) =>
            countries.includes(item.countryCode)
          )
          let remainingCountriesData = data.filter(
            (item) =>
              !countries.includes(item.countryCode) &&
              !finalSelectedAverages.includes(item.countryCode)
          )
          selectedCountriesData.sort((a, b) => {
            if (a.isValueNull && !b.isValueNull) return 1
            if (!a.isValueNull && b.isValueNull) return -1
            return b.y - a.y
          })
          remainingCountriesData.sort((a, b) => {
            if (a.isValueNull && !b.isValueNull) return 1
            if (!a.isValueNull && b.isValueNull) return -1
            return b.y - a.y
          })
          seriesData =
            finalSelectedAverages && finalSelectedAverages.length > 0
              ? [
                  ...data.filter((item) =>
                    finalSelectedAverages.includes(item.countryCode)
                  ),
                  ...selectedCountriesData,
                  ...remainingCountriesData,
                ]
              : [...selectedCountriesData, ...remainingCountriesData]
          categories = seriesData.map((item) => item.name)
        }
        // color scheme logic -- start
        if (selectedCountries.length > 0 && selectedCountries[0] === 'all') {
          const x = [...seriesData]
          seriesData = x.map((data) => {
            return { ...data, color: COLOR_MAP.barChartNoneColor }
          })
        }
        // color scheme logic -- end
      } else {
        seriesData = [...seriesData].sort((a, b) => {
          if (a.isValueNull && !b.isValueNull) return 1
          if (!a.isValueNull && b.isValueNull) return -1
          return b.y - a.y
        })
        categories = seriesData.map((item) => item.name)
      }
    } else if (selectedComparison === NONE && !recentYearSelection) {
      const years =
        selectedMinYear === selectedMaxYear
          ? [selectedMinYear]
          : [selectedMinYear, selectedMaxYear]
      years.forEach((selectedYear) => {
        let tempData = []
        filteredCountries.forEach((country) => {
          const countryName = country.description
          const countryData = indicatorDataForChart.find(
            (data) =>
              data[disaggregations.RefArea] === country.code &&
              data?.data?.xAxis?.some(
                (year) => year.toString() === selectedYear.toString()
              )
          )
          let countryValue = null
          let isValueNull = false
          let isValueOutOfRange = false
          let outOfRangeValue = null
          let countryYear = ''
          if (countryData) {
            const yearIndex = countryData?.data?.xAxis.findIndex(
              (item) => item.toString() === selectedYear.toString()
            )

            countryValue = countryData?.data?.yAxis?.[yearIndex]
            countryYear = selectedYear.toString()
            if (
              countryValue === '' ||
              countryValue === null ||
              countryValue === 'null' ||
              countryValue === 'NaN' ||
              countryValue === undefined
            ) {
              isValueNull = true
              countryValue = 0
            } else {
              if (
                isValueOutOfRangeForRelAxis({
                  axisType,
                  abs_min,
                  abs_max,
                  value: countryValue,
                })
              ) {
                outOfRangeValue = formatValues(countryValue)
                countryValue = 0
                isValueNull = true
                isValueOutOfRange = true
              } else {
                countryValue = formatValues(countryValue)
                allValuesNull = false
              }
            }
          } else {
            isValueNull = true
            countryValue = 0
          }
          tempData.push({
            name: countryName,
            y: countryValue,
            color: COLOR_MAP.barChartNoneColor,
            countryCode: country.code,
            isValueNull,
            isValueOutOfRange,
            outOfRangeValue,
            year: countryYear,
            unitMeasureCode: countryData?.[disaggregations.UnitMeasure],
          })
        })
        seriesData.push({
          name: `${selectedComparison} (${selectedYear})`,
          data: tempData,
        })
      })

      const firstSeriesData = seriesData[0]?.data
      const indices = firstSeriesData.map((_, index) => index)
      indices.sort((a, b) => {
        const selectedCountriesWithoutAll = selectedCountries.filter(
          (c) => c !== 'all'
        )
        const countryA = filteredCountries[a].code
        const countryB = filteredCountries[b].code

        const aIsSelected = selectedCountriesWithoutAll.includes(countryA)
        const bIsSelected = selectedCountriesWithoutAll.includes(countryB)

        if (finalSelectedAverages && finalSelectedAverages.length > 0) {
          if (finalSelectedAverages.includes(countryA)) return -1
          if (finalSelectedAverages.includes(countryB)) return 1
        }

        if (aIsSelected && !bIsSelected) return -1
        if (!aIsSelected && bIsSelected) return 1

        const aData = firstSeriesData[a]
        const bData = firstSeriesData[b]

        const aIsValueNull = aData ? aData.isValueNull : true
        const bIsValueNull = bData ? bData.isValueNull : true

        if (aIsValueNull && !bIsValueNull) return 1
        if (!aIsValueNull && bIsValueNull) return -1
        const aY = aData ? aData.y : 0
        const bY = bData ? bData.y : 0

        return bY - aY
      })
      categories = indices.map((i) => categories[i])

      seriesData = seriesData.map((series) => {
        const data = indices.map((i) => series.data[i])
        return { ...series, data }
      })
    }
  }

  // const badges = getBadges(
  //   selectedFilters,
  //   disaggregationData,
  //   selectedComparison
  // )

  // const aggregates = filteredAggregateData.map((data, index) => {
  //   const valueIndex = data?.data.xAxis.indexOf('' + selectedSingleYear)
  //   let value = data?.data.yAxis[valueIndex]
  //   value = value && value !== 'null' ? formatNumberOnUnitMeasure(data, value) : null

  //   const label =
  //     selectedComparison === NONE
  //       ? `${getNameFromDisaggregation(
  //           disaggregationData,
  //           'REF_AREA',
  //           data['countryCode']
  //         )?.toUpperCase()} AVERAGE ${value}`
  //       : `${getNameFromDisaggregation(
  //           disaggregationData,
  //           'REF_AREA',
  //           data['countryCode']
  //         )?.toUpperCase()} ${getNameFromDisaggregation(
  //           disaggregationData,
  //           selectedComparison,
  //           data[selectedComparison]
  //         )?.toUpperCase()} AVERAGE ${value}`

  //   return { value, label }
  // })
  // const sortedAggregates = aggregates.sort((a, b) => a.value - b.value)
  // const plotLines = sortedAggregates.map((item, index) => {
  //   return {
  //     value: item.value,
  //     dashStyle: 'LongDash',
  //     // color: 'rgba(0, 0, 0, 0.24)',
  //     color: COLOR_MAP['benchmark'],
  //     label: {
  //       text: item.label,
  //       rotation: 0,
  //       style: {
  //         fontWeight: 700,
  //         color: 'rgba(0, 0, 0, 0.42)',
  //       },
  //       verticalAlign: 'bottom',
  //       textAlign: 'center',
  //       y: index * 20 + 10,
  //     },
  //     zIndex: 10,
  //   }
  // })

  let chartHeight = 700
  if (selectedComparison === NONE) {
    if (categories.length === 1) {
      chartHeight = 250
      // if (plotLines.length > 0) {
      //   chartHeight = chartHeight + plotLines * 15
      // }
    } else if (categories.length > 1 && categories.length <= 10) {
      chartHeight = 250 + (categories.length + 1) * 20
      // if (plotLines.length > 0) {
      //   chartHeight = chartHeight + plotLines * 15
      // }
    }
  } else {
    if (seriesData.length === 1 && categories.length === 1) {
      chartHeight = 300
    } else if (seriesData.length > 1 && categories.length === 1) {
      chartHeight = 300 + seriesData.length * 30
    }
    // if (plotLines.length > 0) {
    //   chartHeight = chartHeight + plotLines.length * 15
    // }
  }

  // const subtitleCountries =
  //   selectedCountries[0] === 'all'
  //     ? ['All countries']
  //     : selectedCountries.map(
  //         (selectedCountry) =>
  //           disaggregationCountries.find(
  //             (country) =>
  //               country.id.toLowerCase() === selectedCountry.toLowerCase()
  //           )?.name
  //       )

  if (selectedComparison !== NONE) {
    const legendItemHeight = 20
    const legendRows = Math.ceil(seriesData.length / 3)
    const legendHeight = legendRows * legendItemHeight + 20
    chartHeight += legendHeight
  }

  const barChartOptions = {
    ...BAR_CHART_OPTIONS,
    chart: {
      type: 'bar',
      height: !isDownload
        ? chartHeight
        : categories.length *
          30 *
          (selectedComparison === NONE
            ? recentYearSelection
              ? 1
              : 2
            : seriesData.length),
      scrollablePlotArea: !isDownload
        ? {
            minHeight:
              selectedComparison === NONE
                ? recentYearSelection
                  ? categories?.length * 30
                  : categories?.length * 60
                : categories?.length * 30 * seriesData?.length,
            scrollPositionY: 0,
            opacity: 1,
          }
        : {},
      spacingRight: 25,
    },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    yAxis: {
      title: {
        text: null,
      },
      labels: {
        enabled: true,
        style: { color: 'rgba(0,0,0,0.6)', fontSize: '0.9rem' },
        formatter: function () {
          return formatNumberOnUnitMeasure({
            value: this.value,
            unitMeasure: selectedUnitMeasure,
            decimals: indicatorDataForChart[0]?.[disaggregations.Decimals],
            axis: this.axis,
          })
        },
        y: 10,
      },
      opposite: true,
      gridLineWidth: 0,
      tickWidth: 1,
      lineWidth: seriesData.length > 0 ? 1 : 0,
      tickLength: 5,
      tickColor: COLOR_MAP[ChartElements.ZeroLine],
      lineColor: COLOR_MAP[ChartElements.ZeroLine],
      min: allValuesNull ? 0 : undefined,
      max: allValuesNull ? 100 : undefined,
    },
    xAxis: {
      categories: seriesData.length > 0 ? categories : undefined,
      // Add a small gap before the first category so first bar tooltip doesn't cut for isFullScreenMode.
      min: isFullScreenMode ? -0.7 : 0,
      labels: {
        style: {
          color: '#1A1A1A',
          fontSize: '0.75rem',
        },
        formatter: function () {
          if (typeof this.value === 'number' && this.value < 0) {
            return ''
          }
          return `<b>${('' + this.value)?.toUpperCase()}</b>`
        },
        align: 'left',
        reserveSpace: true,
      },
      scrollbar: {
        enabled: false,
      },
      title: {
        text: null,
      },
      lineWidth: 0,
      // min: 0,
      // max: undefined,
    },
    plotOptions: {
      series: {
        pointWidth: 15,
      },
      bar: {
        dataLabels: {
          enabled: true,
          formatter: function () {
            // if relative axis and country value is out of range of axis_type_params, the show out of range text without bar
            return this.point.isValueOutOfRange
              ? `Out of axis range (${this.point.outOfRangeValue})`
              : this.point.isValueNull
                ? '--'
                 : `${formatNumberOnUnitMeasure({
                    value: this.y,
                    unitMeasure:
                      selectedUnitMeasure || this.point?.unitMeasureCode,
                    decimals:
                      indicatorDataForChart?.[0]?.[disaggregations.Decimals],
                  })} ${this.point.year ? `(${this.point.year})` : ''}`
          },
        },
        animation: false,
        borderRadius: 0,
      },
    },
    series:
      selectedComparison === NONE && recentYearSelection
        ? [
            {
              name: selectedSingleYear.toString(),
              data:
                missingDataType === MissingDataBarTypes.RemoveGaps
                  ? seriesData.map((item) => ({
                      y: item.y,
                      year: item.year,
                      unitMeasureCode: item.unitMeasureCode,
                    }))
                  : seriesData.map((item) => ({
                      y: item.y,
                      isValueNull: item.isValueNull,
                      isValueOutOfRange: item.isValueOutOfRange,
                      outOfRangeValue: item.outOfRangeValue,
                      year: item.year,
                      unitMeasureCode: item.unitMeasureCode,
                    })),
              maxPointWidth: seriesData.length === 1 ? 100 : undefined,
            },
          ]
        : seriesData.map((series) => ({
            ...series,
            data:
              missingDataType === MissingDataBarTypes.RemoveGaps
                ? series.data.map((item) => ({
                    y: item.y,
                    year: item.year,
                    unitMeasureCode: item.unitMeasureCode,
                  }))
                : series.data.map((item) => ({
                    y: item.y,
                    isValueNull: item.isValueNull,
                    isValueOutOfRange: item.isValueOutOfRange,
                    outOfRangeValue: item.outOfRangeValue,
                    year: item.year,
                    unitMeasureCode: item.unitMeasureCode,
                  })),
          })),
    exporting: {
          enabled: false,
        },
      tooltip: {
      backgroundColor: null,
      borderWidth: 0,
      shadow: false,
      useHTML: true,
      outside: !isFullScreenMode,
      style: {
        padding: 0,
      },
      formatter: function () {
        const title = `<div class="tui_chart_tooltip_title" style="font-size: 14px; font-weight: 600; line-height: 16px; color: rgba(0, 0, 0, 0.87); padding-bottom: 4px; margin-bottom: 4px; border-bottom: 1px solid #CED4DE;">${
          this.x
        }, ${
          selectedComparison === NONE
            ? this.point?.year || '--'
            : this.series.name || '--'
        }</div>`
        return `
      <div class="tui_chart_tooltip" style="border: 0.5px solid #CED4DE; background: #FFF; padding: 6px 8px; box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.15); width: auto;">
        ${title}
         <div class="tooltip_country" style="display: flex; align-items: center; min-width: 100px;">
              <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
              ${
                this.y !== null &&
                this.y !== 'null' &&
                this.y !== undefined &&
                this.y !== 'NaN' &&
                this.y !== '' &&
                !this.point.isValueNull
                  ? formatNumberOnUnitMeasure({
                      value: this.y,
                      unitMeasure:
                        selectedUnitMeasure || this.point?.unitMeasureCode,
                      decimals:
                        indicatorDataForChart?.[0]?.[disaggregations.Decimals],
                      isCommaSeparatedFormat: true,
                    }) +
                    (unitMultName ? ` ${unitMultName}` : '') +
                    formatUnitMeasure(
                      selectedUnitMeasure || this.point?.unitMeasureCode,
                      unitMeasureName ||
                        unitMeasureList.find(
                          (item) => item.code === this.point?.unitMeasureCode
                        )?.description ||
                        '',
                      shortLabel ||
                        unitMeasureList.find(
                          (item) => item.code === this.point?.unitMeasureCode
                        )?.short_label ||
                        ''
                    )
                  : '--'
              }
            </span>
            </div>
      </div>
    `
      },
    },
    legend: {
      enabled: selectedComparison !== NONE,
      labelFormatter: function () {
        return this.name.toUpperCase()
      },
      itemStyle: { fontWeight: 'bold' },
      navigation: {
        enabled: false,
      },
    },
  }

  // color scheme logic -- start
  if (selectedComparison === NONE) {
    barChartOptions.plotOptions.bar.colorByPoint = true
    barChartOptions.colors = recentYearSelection
      ? seriesData.map((item) => item.color)
      : seriesData?.[0]?.data?.map((item) => item.color)
  }
  // color scheme logic -- end

  if (missingDataType === MissingDataBarTypes.RemoveGaps) {
    barChartOptions.plotOptions.bar.centerInCategory = true
  }

  // if (plotLines && plotLines.length > 0) {
  //   barChartOptions.yAxis.plotLines = plotLines
  //   barChartOptions.legend.margin = plotLines.length * 20
  // } else {
  //   barChartOptions.yAxis.plotLines = undefined
  // }
  applyAxisRange({
    visualizationMetadata:
      indicatorMetadata?.admin_metadata?.[site]?.visualization,
    chartOptions: barChartOptions,
    axisKey: 'yAxis',
    axisDimension: 'y',
  })
  if (selectedComparison === NONE && categories.length > 20) {
    barChartOptions.exporting.sourceHeight = 675 + 20 * (categories.length - 20)
    barChartOptions.exporting.sourceWidth =
      (675 + 20 * (categories.length - 20)) / 2
  } else if (
    selectedComparison !== NONE &&
    seriesData.length === 1 &&
    categories.length > 20
  ) {
    barChartOptions.exporting.sourceHeight = 675 + 20 * (categories.length - 20)
    barChartOptions.exporting.sourceWidth =
      (675 + 20 * (categories.length - 20)) / 2
  } else if (
    selectedComparison !== NONE &&
    (seriesData.length === 2 || seriesData.length === 3) &&
    categories.length > 6
  ) {
    barChartOptions.exporting.sourceHeight =
      675 + 20 * seriesData.length * categories.length
    barChartOptions.exporting.sourceWidth =
      (675 + 20 * seriesData.length * categories.length) / 2
  }

  return barChartOptions
}
